import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BRANDING } from '../config/branding';

// App Logo
const APP_LOGO = '/lifeflo-logo.png';

// New high-quality app screenshot
const NEW_APP_SCREENSHOT = '/app-screenshot-new.png';

// App screenshot URLs from user's uploads
const APP_SCREENSHOTS = {
  home1: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/r1qlrsor_Home1.png',
  home2: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/99erg76o_Home.png',
  allora: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/ague0h3u_Allora.png',
  health: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/fzdv691c_Health.png',
  yourspace: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/q2uhpr8b_06_yourspace.png',
  // New screenshots
  womensHealth: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/c9e9vdxy_Women%20health.png',
  foodDiary: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/mjey633h_Health3.png',
  life: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/pczg2fay_Life.png',
  work: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/3u045shg_Work.png',
  healthQuickActions: 'https://customer-assets.emergentagent.com/job_a9ce27d3-2863-4487-b830-09f3f703100d/artifacts/hke5g45s_Health2.png',
  // Latest high-quality screenshot
  newHome: '/app-screenshot-new.png',
};

// App Walkthrough Slides
const WALKTHROUGH_SLIDES = [
  {
    id: 'health',
    title: 'Health & Wellness',
    description: 'Track meals, workouts, water intake, and get AI-powered health insights.',
    image: APP_SCREENSHOTS.healthQuickActions,
    color: 'from-emerald-500 to-teal-600',
    features: ['Food Scan', 'Meal Planner', 'Workout Tracking', 'Water Log'],
  },
  {
    id: 'food',
    title: 'Smart Food Diary',
    description: 'Log meals easily with AI food scanning and track your macros effortlessly.',
    image: APP_SCREENSHOTS.foodDiary,
    color: 'from-orange-500 to-amber-600',
    features: ['Calorie Tracking', 'Macro Goals', 'Food Log History', 'AI Analysis'],
  },
  {
    id: 'womens',
    title: "Women's Health",
    description: 'Track your cycle, predict ovulation, and understand your body better.',
    image: APP_SCREENSHOTS.womensHealth,
    color: 'from-pink-500 to-rose-600',
    features: ['Period Tracking', 'Ovulation Prediction', 'Cycle Insights', 'Daily Logs'],
  },
  {
    id: 'work',
    title: 'Work & Productivity',
    description: 'Manage tasks, analyze documents, and boost your productivity with AI.',
    image: APP_SCREENSHOTS.work,
    color: 'from-blue-500 to-indigo-600',
    features: ['Task Management', 'Document Analysis', 'YouTube Summaries', 'Analytics'],
  },
  {
    id: 'life',
    title: 'Life & Finances',
    description: 'Track expenses, manage bills, and get Allora insights for better decisions.',
    image: APP_SCREENSHOTS.life,
    color: 'from-purple-500 to-violet-600',
    features: ['Bill Tracking', 'Expense Categories', 'Budget Overview', 'AI Insights'],
  },
];

// Features data
const FEATURES = [
  {
    icon: '💜',
    title: 'Meet Allora',
    description: 'Your AI companion who remembers you, understands your patterns, and helps you grow.',
    color: 'from-purple-500 to-violet-600',
  },
  {
    icon: '💪',
    title: 'Health & Wellness',
    description: 'Track meals, workouts, sleep, mood, and get personalized health insights.',
    color: 'from-emerald-500 to-teal-600',
  },
  {
    icon: '💼',
    title: 'Work & Focus',
    description: 'Manage tasks, get daily digests, and boost your productivity with AI assistance.',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    icon: '🌱',
    title: 'Life & Habits',
    description: 'Build better habits, track finances, and maintain life balance with smart reminders.',
    color: 'from-amber-500 to-orange-600',
  },
];

// Testimonials
const TESTIMONIALS = [
  {
    quote: "LifeFlo keeps me on track even when my schedule is insane. I finally feel in control.",
    author: "Sarah M.",
    role: "Marketing Manager",
  },
  {
    quote: "I love the food scan — I know exactly what I'm eating without thinking.",
    author: "James K.",
    role: "Fitness Enthusiast",
  },
  {
    quote: "Allora feels like having a personal life coach in my pocket. Game changer!",
    author: "Priya S.",
    role: "Entrepreneur",
  },
];

const LandingPage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white scroll-smooth" style={{ scrollBehavior: 'smooth' }}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <img 
                src={APP_LOGO} 
                alt="LifeFlo AI" 
                className="w-12 h-12 object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect fill="%238B5CF6" width="40" height="40" rx="8"/><text x="20" y="26" font-size="20" text-anchor="middle" fill="white">✨</text></svg>';
                }}
              />
              <span className="text-xl font-bold bg-gradient-to-r from-fuchsia-500 to-violet-500 bg-clip-text text-transparent">LifeFlo AI</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#walkthrough" className="text-gray-600 hover:text-gray-900 transition-colors">App Tour</a>
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 transition-colors">How it Works</a>
            </div>
            <div className="flex items-center gap-3">
              <a href="#download" className="bg-gradient-to-r from-purple-600 to-violet-500 text-white px-5 py-2.5 rounded-full font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all">
                Download App
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-violet-50" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-200/30 to-violet-300/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-teal-200/30 to-emerald-300/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                </span>
                Now available on iOS & Android
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Your AI Companion for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-violet-500">
                  Health, Work & Life
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
                LifeFlo AI helps you stay organized, build healthy habits, and improve your energy, focus, and emotional wellbeing — all in one intelligent app.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <a 
                  href="#download" 
                  className="bg-gradient-to-r from-purple-600 to-violet-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-purple-500/25 transition-all transform hover:-translate-y-0.5"
                >
                  Download the App
                </a>
                <a 
                  href="#how-it-works" 
                  className="flex items-center justify-center gap-2 text-gray-700 px-8 py-4 rounded-full font-semibold text-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  See How It Works
                </a>
              </div>
              
              {/* App store buttons */}
              <div className="flex items-center gap-4 justify-center lg:justify-start">
                <a href="#" className="transition-transform hover:scale-105">
                  <img 
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                    alt="Download on App Store" 
                    className="h-12"
                  />
                </a>
                <a href="#" className="transition-transform hover:scale-105">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                    alt="Get it on Google Play" 
                    className="h-12"
                  />
                </a>
              </div>
            </div>
            
            {/* Right - Phone mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                {/* Main phone */}
                <div className="relative z-10 bg-gray-900 rounded-[3rem] p-3 shadow-2xl shadow-purple-500/30">
                  <div className="bg-white rounded-[2.5rem] overflow-hidden w-[280px] md:w-[320px]">
                    <img 
                      src={APP_SCREENSHOTS.home2} 
                      alt="LifeFlo AI App" 
                      className="w-full"
                    />
                  </div>
                </div>
                {/* Secondary phone */}
                <div className="absolute -left-16 top-20 z-0 bg-gray-900 rounded-[2.5rem] p-2.5 shadow-xl hidden md:block">
                  <div className="bg-white rounded-[2rem] overflow-hidden w-[200px]">
                    <img 
                      src={APP_SCREENSHOTS.allora} 
                      alt="Allora Chat" 
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Life feels scattered. Your apps shouldn't.
            </h2>
            <p className="text-lg text-gray-600">
              Struggling to eat, move, or sleep consistently? Losing track of your goals? Feeling overwhelmed? 
              LifeFlo AI brings everything together in one intelligent app.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '😰', title: 'Overwhelmed', desc: 'Too many apps, too much to track' },
              { icon: '🎯', title: 'Unfocused', desc: 'Goals slip through the cracks' },
              { icon: '😴', title: 'Drained', desc: 'No energy or motivation left' },
            ].map((item, i) => (
              <div key={i} className="text-center p-8 bg-white rounded-2xl shadow-sm">
                <span className="text-5xl mb-4 block">{item.icon}</span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive App Walkthrough Section */}
      <section id="walkthrough" className="py-20 md:py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="animate-pulse">▶</span> Interactive App Tour
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See What LifeFlo AI Can Do
            </h2>
            <p className="text-lg text-gray-600">
              Explore our powerful features designed to simplify your health, work, and life management.
            </p>
          </div>

          {/* Walkthrough Carousel */}
          <div className="relative">
            {/* Main Display */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-12">
              {/* Phone Mockup */}
              <div className="order-2 lg:order-1 flex justify-center">
                <div className="relative">
                  {/* Animated glow effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${WALKTHROUGH_SLIDES[activeSlide].color} rounded-[3rem] blur-2xl opacity-30 animate-pulse`}></div>
                  
                  {/* Phone frame */}
                  <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                    <div className="bg-white rounded-[2.5rem] overflow-hidden w-[280px] md:w-[320px] transition-all duration-500">
                      <img 
                        src={WALKTHROUGH_SLIDES[activeSlide].image} 
                        alt={WALKTHROUGH_SLIDES[activeSlide].title}
                        className="w-full transition-opacity duration-300"
                        key={activeSlide}
                      />
                    </div>
                  </div>
                  
                  {/* Floating badges */}
                  <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                    <span className="text-2xl">✨</span>
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-3 shadow-xl animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }}>
                    <span className="text-2xl">💜</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${WALKTHROUGH_SLIDES[activeSlide].color} text-white px-4 py-2 rounded-full text-sm font-medium mb-6 transition-all duration-300`}>
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {WALKTHROUGH_SLIDES[activeSlide].id.charAt(0).toUpperCase() + WALKTHROUGH_SLIDES[activeSlide].id.slice(1)} Mode
                </div>
                
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 transition-all duration-300">
                  {WALKTHROUGH_SLIDES[activeSlide].title}
                </h3>
                
                <p className="text-lg text-gray-600 mb-8 transition-all duration-300">
                  {WALKTHROUGH_SLIDES[activeSlide].description}
                </p>
                
                {/* Feature pills */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {WALKTHROUGH_SLIDES[activeSlide].features.map((feature, i) => (
                    <span 
                      key={i}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium transition-all duration-300 hover:bg-purple-100 hover:text-purple-700"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Navigation dots */}
                <div className="flex items-center gap-3">
                  {WALKTHROUGH_SLIDES.map((slide, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveSlide(i)}
                      className={`transition-all duration-300 ${
                        i === activeSlide 
                          ? `w-12 h-3 rounded-full bg-gradient-to-r ${slide.color}` 
                          : 'w-3 h-3 rounded-full bg-gray-200 hover:bg-gray-300'
                      }`}
                      aria-label={`Go to ${slide.title}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Feature Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
              {WALKTHROUGH_SLIDES.map((slide, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSlide(i)}
                  className={`p-4 rounded-2xl text-left transition-all duration-300 ${
                    i === activeSlide 
                      ? `bg-gradient-to-br ${slide.color} text-white shadow-lg scale-105` 
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="text-2xl mb-2">
                    {i === 0 ? '💪' : i === 1 ? '🍎' : i === 2 ? '💗' : i === 3 ? '💼' : '💰'}
                  </div>
                  <div className={`text-sm font-semibold ${i === activeSlide ? 'text-white' : 'text-gray-900'}`}>
                    {slide.title.split(' ')[0]}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Allora Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-purple-900 via-violet-900 to-purple-800 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
                <span>💜</span> AI-Powered Emotional Intelligence
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Meet Allora — Your Intelligent Life Partner
              </h2>
              <p className="text-lg text-purple-100 mb-8">
                Allora is more than an AI assistant. She remembers your habits, routines, and preferences. 
                She tracks your health, emotional wellbeing, and productivity. She gives you personalized daily insights and gentle nudges.
              </p>
              
              <div className="space-y-4">
                {[
                  'Remembers your patterns and preferences',
                  'Provides personalized daily insights',
                  'Helps plan meals, workouts & life goals',
                  'Supports your emotional wellbeing',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-400/30 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-200" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-purple-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative flex justify-center">
              <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="bg-white rounded-[2.5rem] overflow-hidden w-[280px] md:w-[320px]">
                  <img 
                    src={APP_SCREENSHOTS.allora} 
                    alt="Allora AI Chat" 
                    className="w-full"
                  />
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl">
                <span className="text-2xl">✨</span>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl">
                <span className="text-2xl">💜</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              One App. Every Aspect of Your Life.
            </h2>
            <p className="text-lg text-gray-600">
              Health, work, and life — all connected through intelligent AI that understands you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, i) => (
              <div 
                key={i} 
                className="group relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-100"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
          
          {/* Feature showcase */}
          <div className="mt-20 grid lg:grid-cols-3 gap-8">
            {[
              { img: APP_SCREENSHOTS.health, title: 'Health Dashboard', desc: 'Track water, calories, workouts, and more' },
              { img: APP_SCREENSHOTS.home1, title: 'Smart Home', desc: 'Your daily command center' },
              { img: APP_SCREENSHOTS.yourspace, title: 'YourSpace', desc: 'Personal AI assistant for anything' },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-3xl p-6 text-center">
                <div className="bg-gray-900 rounded-[2rem] p-2 inline-block mb-4 shadow-lg">
                  <div className="bg-white rounded-[1.5rem] overflow-hidden w-[200px]">
                    <img src={item.img} alt={item.title} className="w-full" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in 3 Simple Steps
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Download LifeFlo AI', desc: 'Available on iOS and Android. Start your free 7-day trial.' },
              { step: '2', title: 'Tell Allora About You', desc: 'Share your goals, routines, and what matters most to you.' },
              { step: '3', title: 'Get Smart Insights', desc: 'Receive personalized guidance and watch your life transform.' },
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-3xl p-8 text-center relative z-10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-violet-500 text-white font-bold text-xl flex items-center justify-center mx-auto mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 text-purple-300 z-0">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-3xl p-8 md:p-12 text-center">
              <div className="text-5xl mb-6">"</div>
              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                {TESTIMONIALS[currentTestimonial].quote}
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center text-white font-semibold">
                  {TESTIMONIALS[currentTestimonial].author[0]}
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{TESTIMONIALS[currentTestimonial].author}</p>
                  <p className="text-sm text-gray-600">{TESTIMONIALS[currentTestimonial].role}</p>
                </div>
              </div>
              
              {/* Dots */}
              <div className="flex items-center justify-center gap-2 mt-8">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentTestimonial ? 'w-8 bg-purple-600' : 'bg-purple-200'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="py-20 md:py-32 bg-gradient-to-br from-purple-900 via-violet-900 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            One App. Total Life Clarity.
          </h2>
          <p className="text-xl text-purple-200 mb-10 max-w-2xl mx-auto">
            Smarter habits. Better energy. More balance. LifeFlo AI adapts to your life — so you don't have to adapt to it.
          </p>
          <p className="text-lg text-purple-300 mb-8">
            Download LifeFlo AI now and start your journey to a better life.
          </p>
          <div className="flex items-center gap-4 justify-center">
            <a href="#" className="transition-transform hover:scale-105">
              <img 
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                alt="Download on App Store" 
                className="h-14"
              />
            </a>
            <a href="#" className="transition-transform hover:scale-105">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                className="h-14"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={APP_LOGO} 
                  alt="LifeFlo AI" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect fill="%238B5CF6" width="40" height="40" rx="8"/><text x="20" y="26" font-size="20" text-anchor="middle" fill="white">✨</text></svg>';
                  }}
                />
                <span className="text-xl font-bold bg-gradient-to-r from-fuchsia-400 to-violet-400 bg-clip-text text-transparent">LifeFlo AI</span>
              </div>
              <p className="text-sm">Your AI companion for health, work, and life.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#walkthrough" className="hover:text-white transition-colors">App Tour</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#download" className="hover:text-white transition-colors">Download</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} LifeFlo AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
